import React, { createContext, ReactNode, useContext, useState } from 'react'
import type { ProductApiData } from '../types'
import { normalizeProductData } from '../utils'

/** Fora do react-hook-form: quem edita é o `AddProductForm`, cujo contrato é `order` + `setOrder`. */
export const useOrderItemsState = () => {
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null)
  const [productToEdit, setProductToEdit] = useState<ProductApiData | null>(null)
  const [openProductIndex, setOpenProductIndex] = useState<number | null>(null)

  const [kitCount, setKitCount] = useState(0)
  const [showAddProductForSection, setShowAddProductForSection] = useState<'standalone' | number | null>(null)
  const [openKits, setOpenKits] = useState<{ [key: number]: boolean }>({})
  const [isKitMode, setIsKitMode] = useState(false)
  const [isItemsSectionOpen, setIsItemsSectionOpen] = useState(true)

  const closeProductEdit = () => {
    if (typeof showAddProductForSection === 'number') {
      const currentKitIdx = showAddProductForSection;

      const hasProductsInKit = addedProducts.some(
        (p) => p.kitIndex === currentKitIdx
      );

      if (!hasProductsInKit) {
        setKitCount((prev) => Math.max(0, prev - 1));
      }
    }


    setEditingProductIndex(null);
    setProductToEdit(null);
    setOpenProductIndex(null);
    setShowAddProductForSection(null);
  };

  const handleRemoveProduct = (index: number) => {
    setAddedProducts((prev) => prev.filter((_, i) => i !== index))
    closeProductEdit()
  }

  const handleProductUpdated = (_: ProductApiData, updated: ProductApiData) => {
    const idx = editingProductIndex ?? openProductIndex
    if (idx === null) return
    setAddedProducts((prev) => {
      const next = [...prev]
      next[idx] = normalizeProductData({ ...updated, kitIndex: prev[idx].kitIndex })
      return next
    })
    closeProductEdit()
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    if (newProduct.isKitIntent) {
      if (!newProduct.fashionProductId) {
        if (showAddProductForSection === 'standalone') {
          const nextKitIdx = kitCount;
          setKitCount(prev => prev + 1);
          setShowAddProductForSection(nextKitIdx);
        }
        else {
          setShowAddProductForSection(null);
        }
        return;
      }


      if (showAddProductForSection === 'standalone') {
        const newIdx = kitCount;
        setKitCount((prev) => prev + 1);

        setAddedProducts((prev) => [
          ...prev,
          normalizeProductData({ ...newProduct, kitIndex: newIdx })
        ]);

        setShowAddProductForSection(null);
        return;
      }

      const targetKitIdx = typeof showAddProductForSection === 'number'
        ? showAddProductForSection
        : kitCount - 1;

      setAddedProducts((prev) => [
        ...prev,
        normalizeProductData({ ...newProduct, kitIndex: targetKitIdx })
      ]);

      setShowAddProductForSection(null);
    } else {
      setAddedProducts((prev) => [
        ...prev,
        normalizeProductData({ ...newProduct, kitIndex: null })
      ]);
      setShowAddProductForSection(null);
    }
  };

  const handleRemoveKit = (kitIdx: number) => {
    setAddedProducts((prev) =>
      prev
        .filter((p) => p.kitIndex !== kitIdx)
        .map((p) => {
          if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > kitIdx)
            return { ...p, kitIndex: p.kitIndex - 1 }
          return p
        })
    )
    setKitCount((prev) => prev - 1)
    closeProductEdit()
  }

  return {
    addedProducts,
    setAddedProducts,
    editingProductIndex,
    setEditingProductIndex,
    productToEdit,
    setProductToEdit,
    openProductIndex,
    setOpenProductIndex,
    kitCount,
    setKitCount,
    showAddProductForSection,
    setShowAddProductForSection,
    openKits,
    setOpenKits,
    isKitMode,
    setIsKitMode,
    isItemsSectionOpen,
    setIsItemsSectionOpen,
    closeProductEdit,
    handleRemoveProduct,
    handleProductUpdated,
    handleNewProductAdded,
    handleRemoveKit,
  }
}

export type OrderItemsContextType = ReturnType<typeof useOrderItemsState>

const OrderItemsContext = createContext<OrderItemsContextType | null>(null)

export const OrderItemsProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <OrderItemsContext.Provider value={useOrderItemsState()}>
    {children}
  </OrderItemsContext.Provider>
)

export const useOrderItems = () => {
  const context = useContext(OrderItemsContext)
  if (!context) {
    throw new Error('useOrderItems must be used within an OrderItemsProvider')
  }
  return context
}
